import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"
import _imports_0 from '@images/location-icon.svg'


const _hoisted_1 = {
  key: 0,
  class: "confirmation"
}
const _hoisted_2 = { class: "confirmation-info" }
const _hoisted_3 = { class: "h5" }
const _hoisted_4 = { "data-testid": "test-drive-confirmationHelperText" }
const _hoisted_5 = { class: "appointment-info" }
const _hoisted_6 = { class: "location-info" }
const _hoisted_7 = ["alt"]
const _hoisted_8 = { class: "details" }
const _hoisted_9 = ["href"]
const _hoisted_10 = { class: "calendar-button" }
const _hoisted_11 = { class: "value-props" }
const _hoisted_12 = {
  "data-testid": "test-drive-valuePropsTitle",
  class: "h6"
}
const _hoisted_13 = {
  "data-testid": "test-drive-valuePropsHelperText",
  class: "small"
}
const _hoisted_14 = { class: "content" }
const _hoisted_15 = { class: "sub-head" }
const _hoisted_16 = { class: "value-prop-text" }
const _hoisted_17 = { class: "confirmation-footer" }
const _hoisted_18 = { class: "info" }
const _hoisted_19 = {
  "data-testid": "test-drive-confirmationFooterTitle",
  class: "h6"
}
const _hoisted_20 = {
  "data-testid": "test-drive-confirmationFooterText",
  class: "small"
}

import { formatDealerId } from '@/util/scheduleTestDriveUtil';

import { defineComponent, PropType } from 'vue';
import ScheduleTestDriveVehicleInfo from '@components/Page/ScheduleTestDrive/ScheduleTestDrive--VehicleInfo.vue';
import { mapActions, mapState } from 'pinia';
import { useScheduleTestDriveStore } from '@/stores/scheduleTestDrive';
import { DateTime } from 'luxon';
import { FieldsPropType } from '@/lib/FieldTypes';

const __default__ = defineComponent({
  name: 'ScheduleTestDrive--Confirmation',
  components: { ScheduleTestDriveVehicleInfo },
  props: {
    fields: {
      type: Object as PropType<FieldsPropType>,
      default: () => ({}),
    },
    rendering: {
      type: Object,
      default: () => ({}),
    },
    params: {
      type: Object,
      default: () => ({}),
    },
    errorComponent: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    ...mapState(useScheduleTestDriveStore, [
      'contact',
      'day',
      'time',
      'view',
      'vehicle',
      'dealership',
    ]),
  },
  data() {
    return {
      valueProps: [
        {
          icon: 'ep-icon-car',
          title: 'Buying/Test driving',
          text: 'Driver’s license & insurance',
        },
        { icon: 'ep-icon-bank', title: 'Financing', text: 'Paycheck stub & utility bill' },
        {
          icon: 'ep-icon-cash',
          title: 'Selling/Trading',
          text: 'Photo ID, title/payoff & keys',
        },
      ],
    };
  },
  methods: {
    ...mapActions(useScheduleTestDriveStore, [
      'setTestDriveContact',
      'setTestDriveDay',
      'setTestDriveTime',
      'setTestDriveView',
    ]),
    getLocaleDay(day: string) {
      const normalizedDay = day.replaceAll('-', '/');
      const date = new Date(`${normalizedDay} GMT-12:00`); // we don't want timezone to be a factor in the simple date conversion
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: undefined,
        month: 'long',
        day: 'numeric',
      });
    },
    getCalendarHref() {
      return `data:text/calendar;charset=utf8,BEGIN:VCALENDAR%0AVERSION:2.0%0ABEGIN:VTIMEZONE%0ATZID:UTC%0ABEGIN:STANDARD%0ADTSTART:16010101T000000%0ATZOFFSETFROM:-0000%0ATZOFFSETTO:-0000%0AEND:STANDARD%0AEND:VTIMEZONE%0ABEGIN:VEVENT%0ADTSTART;TZID=UTC:${this.getStartTime()}%0ADTEND;TZID=UTC:${this.getEndTime()}%0ASUMMARY:${this.fields.calendarTitle.value}%0ADESCRIPTION:${this.fields.calendarDescription.value}%0ALOCATION:${this.dealership.address}, ${this.dealership.city}, ${this.dealership.state} ${this.dealership.zip}%0AEND:VEVENT%0AEND:VCALENDAR`;
    },
    convertToUTC(day: string, time: string): { utcStartTime: string; utcEndTime: string } {
      // Splitting the time into start and end segments on the en dash.
      const [startSegment, endSegment] = time.split('–');

      const convertTime = (timeSegment, period) => {
        let [hour, minute = '00'] = timeSegment.trim().split(':');
        hour = parseInt(hour);

        if (period.toLowerCase() === 'pm' && hour !== 12) hour += 12;
        if (period.toLowerCase() === 'am' && hour === 12) hour = 0;

        const timeZone = this.dealership.timeZone;

        const utcTime =
          DateTime.fromISO(
            `${day}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`,
            {
              zone: timeZone,
            }
          ) ?? '';

        // Specific date format for ics
        return utcTime.toUTC().toFormat("yyyyMMdd'T'HHmmss");
      };

      const [startTime, startPeriod] = startSegment.trim().split(' ');
      const [endTime, endPeriod] = endSegment.trim().split(' ');

      const utcStartTime = convertTime(startTime, startPeriod);
      const utcEndTime = convertTime(endTime, endPeriod);

      return { utcStartTime, utcEndTime };
    },
    getStartTime() {
      const { utcStartTime } = this.convertToUTC(this.day, this.time);
      return utcStartTime;
    },
    getEndTime() {
      const { utcEndTime } = this.convertToUTC(this.day, this.time);
      return utcEndTime;
    },
    getDirectionsLink() {
      const address = `${this.dealership.address}, ${this.dealership.city}, ${this.dealership.state} ${this.dealership.zip}`;
      return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    },
    redirectToDealer() {
      const baseUrl = window.location.href.split('?')[0];
      const queryParams = new URLSearchParams(window.location.search);

      queryParams.delete('radius');
      queryParams.set('ship', 'false');
      // @ts-ignore TODO-pinia existing error
      queryParams.set('dealer', this.dealership.dealerId.replace(/\s+/g, ''));

      const newUrl = `${baseUrl}?${queryParams.toString()}`;
      window.open(newUrl, '_blank');
    },
  },
});


export default /*#__PURE__*/_defineComponent({
  ...__default__,
  setup(__props) {


return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_icon = _resolveComponent("v-icon")!

  return (_ctx.view === 'confirmation')
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createElementVNode("div", _hoisted_2, [
          _createElementVNode("p", _hoisted_3, _toDisplayString(`You're all set, ${_ctx.contact?.first}! 🎉`), 1),
          _createElementVNode("p", _hoisted_4, _toDisplayString(_ctx.fields.helperText.value), 1),
          _createElementVNode("div", _hoisted_5, [
            _createElementVNode("div", _hoisted_6, [
              _createElementVNode("img", {
                src: _imports_0,
                alt: _ctx.dealership.storeName
              }, null, 8, _hoisted_7),
              _createElementVNode("div", _hoisted_8, [
                _createElementVNode("a", {
                  href: _ctx.getDirectionsLink(),
                  target: "_blank"
                }, _toDisplayString(_unref(formatDealerId)(_ctx.dealership?.dealerId ?? '')), 9, _hoisted_9),
                _createElementVNode("p", null, _toDisplayString(_ctx.getLocaleDay(_ctx.day)), 1),
                _createElementVNode("p", null, _toDisplayString(_ctx.time), 1)
              ])
            ]),
            _createVNode(ScheduleTestDriveVehicleInfo)
          ]),
          _createElementVNode("div", _hoisted_10, [
            _createVNode(_component_v_btn, {
              href: _ctx.getCalendarHref(),
              download: "",
              "data-testid": "test-drive-addToCalendarButton"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_ctx.fields.buttonText.value), 1)
              ]),
              _: 1
            }, 8, ["href"])
          ]),
          _createElementVNode("div", _hoisted_11, [
            _createElementVNode("p", _hoisted_12, _toDisplayString(_ctx.fields.valuePropsTitle.value), 1),
            _createElementVNode("p", _hoisted_13, _toDisplayString(_ctx.fields.valuePropsHelperText.value), 1),
            _createElementVNode("div", _hoisted_14, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.valueProps, (prop) => {
                return (_openBlock(), _createElementBlock("div", {
                  class: "value-prop",
                  key: prop.title
                }, [
                  _createElementVNode("p", _hoisted_15, [
                    _createVNode(_component_v_icon, {
                      icon: prop.icon
                    }, null, 8, ["icon"]),
                    _createTextVNode(_toDisplayString(prop.title), 1)
                  ]),
                  _createElementVNode("p", _hoisted_16, _toDisplayString(prop.text), 1)
                ]))
              }), 128))
            ])
          ])
        ]),
        _createElementVNode("div", _hoisted_17, [
          _createElementVNode("div", _hoisted_18, [
            _createElementVNode("p", _hoisted_19, _toDisplayString(_ctx.fields.footerTitle.value) + " " + _toDisplayString(_ctx.dealership.storeName), 1),
            _createElementVNode("p", _hoisted_20, _toDisplayString(_ctx.fields.footerText.value), 1)
          ]),
          _createVNode(_component_v_btn, {
            class: "secondary",
            onClick: _ctx.redirectToDealer,
            "data-testid": "test-drive-testDriveMoreButton"
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_ctx.fields.footerButtonText.value), 1)
            ]),
            _: 1
          }, 8, ["onClick"])
        ])
      ]))
    : _createCommentVNode("", true)
}
}

})